import React, { useMemo } from 'react';
import { CalendarClock } from 'lucide-react';

/**
 * Displays the elapsed time since a lease start date in years, months and days.
 * Recomputes on each render (page opens or refresh).
 *
 * Props:
 * - startDate: ISO string or Date
 * - variant: "compact" (inline text) | "card" (default, boxed)
 * - accentColor: tailwind color class for the card border/text (e.g. "amber", "emerald")
 * - label: optional heading (default: "Ancienneté du bail")
 */
export function computeLeaseDuration(startDate, endDate) {
  if (!startDate) return null;
  let start;
  try {
    start = typeof startDate === 'string' ? new Date(startDate) : startDate;
    if (Number.isNaN(start.getTime())) return null;
  } catch {
    return null;
  }
  // If lease is terminated, freeze duration at end_date (else use "now")
  let now = new Date();
  if (endDate) {
    try {
      const end = typeof endDate === 'string' ? new Date(endDate) : endDate;
      if (!Number.isNaN(end.getTime())) now = end;
    } catch {
      // ignore parse errors, fall back to now
    }
  }
  if (start > now) return { years: 0, months: 0, days: 0, totalDays: 0, frozen: !!endDate };

  let years = now.getFullYear() - start.getFullYear();
  let months = now.getMonth() - start.getMonth();
  let days = now.getDate() - start.getDate();

  if (days < 0) {
    // borrow days from previous month
    months -= 1;
    const prev = new Date(now.getFullYear(), now.getMonth(), 0);
    days += prev.getDate();
  }
  if (months < 0) {
    years -= 1;
    months += 12;
  }
  const totalDays = Math.floor((now - start) / (1000 * 60 * 60 * 24));
  return { years, months, days, totalDays, frozen: !!endDate };
}

const Unit = ({ value, label, accent }) => (
  <div className="flex flex-col items-center px-2.5" data-testid={`lease-duration-${label}`}>
    <div
      className={`text-2xl md:text-3xl font-bold tabular-nums leading-none ${accent}`}
      style={{ fontFamily: "'Outfit', 'Inter', sans-serif" }}
    >
      {String(value).padStart(2, '0')}
    </div>
    <div className="text-[10px] uppercase tracking-widest text-gray-500 mt-1 font-semibold">
      {label}
    </div>
  </div>
);

const LeaseDurationCounter = ({
  startDate,
  endDate = null,
  variant = 'card',
  accentColor = 'amber',
  label = 'Ancienneté du bail',
}) => {
  const dur = useMemo(() => computeLeaseDuration(startDate, endDate), [startDate, endDate]);

  if (!dur) return null;

  const accentText = {
    amber: 'text-amber-700',
    emerald: 'text-emerald-700',
    blue: 'text-blue-700',
    rose: 'text-rose-700',
  }[accentColor] || 'text-amber-700';
  const accentBorder = {
    amber: 'border-amber-200 bg-amber-50/60',
    emerald: 'border-emerald-200 bg-emerald-50/60',
    blue: 'border-blue-200 bg-blue-50/60',
    rose: 'border-rose-200 bg-rose-50/60',
  }[accentColor] || 'border-amber-200 bg-amber-50/60';
  const accentIcon = {
    amber: 'text-amber-600',
    emerald: 'text-emerald-600',
    blue: 'text-blue-600',
    rose: 'text-rose-600',
  }[accentColor] || 'text-amber-600';

  if (variant === 'compact') {
    return (
      <span
        className={`inline-flex items-center gap-1.5 text-xs font-medium ${accentText}`}
        data-testid="lease-duration-compact"
        title={
          dur.frozen
            ? `Bail terminé le ${new Date(endDate).toLocaleDateString('fr-FR')} — ${dur.totalDays} jour(s)`
            : `Depuis le ${new Date(startDate).toLocaleDateString('fr-FR')} - ${dur.totalDays} jour(s)`
        }
      >
        <CalendarClock className={`w-3.5 h-3.5 ${accentIcon}`} />
        {dur.years > 0 && `${dur.years} an${dur.years > 1 ? 's' : ''} `}
        {(dur.years > 0 || dur.months > 0) && `${dur.months} mois `}
        {`${dur.days} j`}
        {dur.frozen && <span className="ml-1 text-[9px] font-bold uppercase tracking-wider opacity-70">(figé)</span>}
      </span>
    );
  }

  return (
    <div
      className={`rounded-xl border-2 ${accentBorder} p-3 ${dur.frozen ? 'opacity-90' : ''}`}
      data-testid="lease-duration-card"
    >
      <div className="flex items-center justify-between gap-1.5 mb-2">
        <div className="flex items-center gap-1.5">
          <CalendarClock className={`w-4 h-4 ${accentIcon}`} />
          <span className="text-[11px] uppercase tracking-wider font-bold text-gray-700">
            {label}
          </span>
        </div>
        {dur.frozen && (
          <span className="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-gray-700 text-white">
            Figé — bail terminé
          </span>
        )}
      </div>
      <div className="flex items-center justify-around divide-x divide-gray-200/70">
        <Unit value={dur.years} label="ans" accent={accentText} />
        <Unit value={dur.months} label="mois" accent={accentText} />
        <Unit value={dur.days} label="jours" accent={accentText} />
      </div>
      <div className="text-[10px] text-gray-500 text-center mt-2 font-mono">
        {dur.frozen ? (
          <>Du {new Date(startDate).toLocaleDateString('fr-FR')} au {new Date(endDate).toLocaleDateString('fr-FR')} — {dur.totalDays} jour(s) au total</>
        ) : (
          <>Depuis le {new Date(startDate).toLocaleDateString('fr-FR')} — {dur.totalDays} jour(s) au total</>
        )}
      </div>
    </div>
  );
};

export default LeaseDurationCounter;
